import { useEffect, useRef, useState } from "react";

/*
 * antd sizes a scrollable table body from `scroll.y`, which it inlines as a
 * max-height. A percentage there is meaningless, so a table inside a flex
 * column can't grow into the space it was given by CSS alone.
 *
 * This measures the slot the table actually occupies and returns a pixel
 * budget for the body, minus the header and pagination that sit inside it.
 */
export default function useFlexTableHeight(
  enabled = true,
  reservedPx = 96,
  minPx = 120,
): [React.RefObject<HTMLDivElement>, number | undefined] {
  const containerRef = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState<number | undefined>(undefined);

  useEffect(() => {
    const node = containerRef.current;
    if (!node || !enabled) {
      return;
    }

    const measure = () => {
      const available = node.getBoundingClientRect().height - reservedPx;
      setHeight(Math.max(minPx, Math.round(available)));
    };

    measure();

    const observer = new ResizeObserver(measure);
    observer.observe(node);
    window.addEventListener("resize", measure);

    return () => {
      observer.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, [enabled, reservedPx, minPx]);

  return [containerRef, height];
}
